import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, Tooltip, Pagination } from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import Spin from '../Spin';
import './PdfViewer.scss';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

export default function PdfViewer({ pdf }) {
  const containerRef = useRef(null);
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);
  const [pageWidth, setPageWidth] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Función para manejar carga exitosa del documento
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setIsLoading(false);
  }

  // Funciones para controlar el zoom
  const zoomIn = () => {
    if (scale < 2.5) setScale((prevScale) => parseFloat((prevScale + 0.1).toFixed(1)));
  };

  const zoomOut = () => {
    if (scale > 0.5) setScale((prevScale) => parseFloat((prevScale - 0.1).toFixed(1)));
  };

  // Funciones para navegación entre páginas
  const goToNextPage = () => {
    if (currentPage < numPages) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  // Función para cambiar de página
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  // Función para modo pantalla completa
  const toggleFullscreen = () => {
    const viewerElement = document.getElementById('pdf-viewer-container');

    if (!isFullscreen) {
      if (viewerElement.requestFullscreen) {
        viewerElement.requestFullscreen();
      } else if (viewerElement.webkitRequestFullscreen) {
        viewerElement.webkitRequestFullscreen();
      } else if (viewerElement.msRequestFullscreen) {
        viewerElement.msRequestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      }
    }

    setIsFullscreen(!isFullscreen);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return undefined;
    }

    const updateWidth = () => {
      const styles = window.getComputedStyle(el);
      const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const nextWidth = Math.floor(el.clientWidth - paddingX);
      if (nextWidth > 0) {
        setPageWidth((prev) => (Math.abs((prev || 0) - nextWidth) > 2 ? nextWidth : prev));
      }
    };

    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, [pdf, isFullscreen]);

  // Agregar listeners para detectar cambios en pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(
        document.fullscreenElement ||
          document.webkitFullscreenElement ||
          document.mozFullScreenElement ||
          document.msFullscreenElement
      );
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  if (!pdf) {
    return <p>Seleccione un informe para visualizar</p>;
  }

  return (
    <div id="pdf-viewer-container" className="pdf-viewer-container">
      {/* Barra de herramientas */}
      <div className="pdf-toolbar">
        <div className="page-navigation">
          <Tooltip title="Página anterior">
            <Button
              disabled={currentPage <= 1}
              onClick={goToPreviousPage}
              icon={<ArrowLeftOutlined />}
            />
          </Tooltip>

          <Pagination
            simple
            current={currentPage}
            total={numPages}
            pageSize={1}
            onChange={onPageChange}
            size="small"
            showSizeChanger={false}
          />

          <Tooltip title="Página siguiente">
            <Button
              disabled={currentPage >= numPages}
              onClick={goToNextPage}
              icon={<ArrowRightOutlined />}
            />
          </Tooltip>
        </div>

        <div className="zoom-controls">
          <Tooltip title="Reducir">
            <Button onClick={zoomOut} disabled={scale <= 0.5} icon={<ZoomOutOutlined />} />
          </Tooltip>

          <span className="zoom-level">{Math.round(scale * 100)}%</span>

          <Tooltip title="Ampliar">
            <Button onClick={zoomIn} disabled={scale >= 2.5} icon={<ZoomInOutlined />} />
          </Tooltip>

          <Tooltip title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
            <Button onClick={toggleFullscreen} icon={<FullscreenOutlined />} />
          </Tooltip>

          <Tooltip title="Descargar">
            <a href={pdf} target="_blank" rel="noopener noreferrer" download>
              <Button icon={<DownloadOutlined />} />
            </a>
          </Tooltip>
        </div>
      </div>

      {/* Contenedor del documento */}
      <div ref={containerRef} className="pdf-document-container">
        <Document
          file={pdf}
          loading={
            <div className="pdf-loading">
              <Spin />
              <span>Cargando documento...</span>
            </div>
          }
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={() => setIsLoading(false)}
          className="pdf-document"
        >
          {isLoading ? (
            <div className="pdf-loading">
              <LoadingOutlined style={{ fontSize: 24 }} spin />
              <span>Cargando PDF...</span>
            </div>
          ) : (
            pageWidth && (
              <Page
                pageNumber={currentPage}
                width={pageWidth}
                scale={scale}
                loading={
                  <div className="page-loading">
                    <LoadingOutlined spin />
                  </div>
                }
                className="pdf-page"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            )
          )}
        </Document>

        {!isLoading && numPages && (
          <div className="page-indicator">
            Página {currentPage} de {numPages}
          </div>
        )}
      </div>
    </div>
  );
}
